/**
 * Brings a part of a card into view when it opens (B153): a tray under a tile, or a fold under a row, that opens below the
 * screen's edge is scrolled into sight, smoothly and only as far as needed; one already fully in sight does not move the page.
 * Reduced motion scrolls at once. One shared piece for the plant card's trays, the bed card's folds and the card's own door
 * scroll, so the way the page follows an opening is changed in one place.
 *
 * Who uses it: CardTiles (the To-dos and Notes trays, only inside a pop-up: the Plants page's own card keeps its door scroll),
 * PlantCard (the trays' door on the page), LocationCard (the bed's Notes and Plants folds), OwnTodosRow (the bed's to-do fold).
 *
 * @module useBringIntoView
 */

import React from 'react';

/**
 * Scrolls an element into view, only as far as needed.
 *
 * @param el - The element to show; nothing happens for none.
 */
export function bringIntoView(el: Element | null | undefined): void {
  if (!el) return;
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
}

/**
 * A ref for the part that opens: when `open` turns true after the first draw, the part is brought into view once it is drawn.
 * Something open from the first draw is not scrolled to (the page has just loaded).
 *
 * @param open - Whether the part is open.
 * @param inDialogOnly - Scroll only when the part sits inside a dialog (the plant pop-up's own scroller, where the bug was).
 * @returns The ref to put on the opened part.
 */
export function useBringIntoView<T extends HTMLElement = HTMLDivElement>(open: boolean, inDialogOnly = false): React.RefObject<T> {
  const ref = React.useRef<T>(null);
  /** Whether it was open at the last draw. */
  const was = React.useRef(open);
  React.useEffect(() => {
    if (open && !was.current) {
      /** After the browser has laid the opened part out. */
      const frame = requestAnimationFrame(() => {
        if (!inDialogOnly || ref.current?.closest('[role="dialog"]')) bringIntoView(ref.current);
      });
      was.current = open;
      return () => cancelAnimationFrame(frame);
    }
    was.current = open;
    return undefined;
  }, [open, inDialogOnly]);
  return ref;
}
